import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, CalendarClock, LogIn, LogOut, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { PageHeader, EmptyState, Loading } from "@/components/dash/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const ROLES = ["Head Chef", "Sous Chef", "Server", "Bartender", "Host", "Manager", "Dishwasher"];
const empty = { name: "", role: "Server", email: "", phone: "", hourly_rate: "", status: "active" };

export default function Employees() {
  const [emps, setEmps] = useState(null);
  const [logs, setLogs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    const [e, l] = await Promise.all([api.get("/employees"), api.get("/timeclock")]);
    setEmps(e.data); setLogs(l.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const activeLog = (empId) => logs.find((l) => l.employee_id === empId && !l.clock_out);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/employees", { ...form, hourly_rate: parseFloat(form.hourly_rate) || 0 });
      toast.success("Staff added"); setForm(empty); setOpen(false); load();
    } catch (err) { toast.error(apiErr(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this staff member?")) return;
    await api.delete(`/employees/${id}`); toast.success("Removed"); load();
  };

  const toggleClock = async (emp) => {
    const isIn = activeLog(emp.id);
    try {
      await api.post(`/timeclock/${isIn ? "clockout" : "clockin"}`, { employee_id: emp.id });
      toast.success(isIn ? `${emp.name} clocked out` : `${emp.name} clocked in`);
      load();
    } catch (err) { toast.error(apiErr(err)); }
  };

  if (!emps) return <Loading />;
  const clockedIn = emps.filter((e) => activeLog(e.id));

  return (
    <div data-testid="employees-page">
      <PageHeader title="Staff & Time Clock" subtitle="Manage your team and track who's on shift."
        action={<Button data-testid="add-employee-btn" onClick={() => setOpen(true)} className="bg-primary hover:bg-[#B35A42] text-primary-foreground"><Plus className="h-4 w-4 mr-1" /> Add staff</Button>} />

      <div className="bg-secondary/10 border border-secondary/20 rounded-xl p-4 mb-6 flex items-center gap-3">
        <Clock className="h-5 w-5 text-secondary" />
        <p className="text-sm"><span className="font-semibold">{clockedIn.length}</span> staff currently on shift{clockedIn.length ? `: ${clockedIn.map((e) => e.name.split(" ")[0]).join(", ")}` : ""}</p>
      </div>

      {emps.length === 0 ? (
        <EmptyState icon={CalendarClock} title="No staff yet" desc="Add your team members to manage shifts and time tracking."
          action={<Button onClick={() => setOpen(true)} className="bg-primary text-primary-foreground"><Plus className="h-4 w-4 mr-1" /> Add staff</Button>} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {emps.map((emp) => {
            const isIn = activeLog(emp.id);
            return (
              <div key={emp.id} data-testid={`employee-${emp.id}`} className="bg-card border border-border rounded-xl p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium">{emp.name}</p>
                    <p className="text-sm text-muted-foreground">{emp.role}</p>
                  </div>
                  {isIn ? <Badge className="bg-secondary/15 text-secondary border-0 hover:bg-secondary/15">On shift</Badge>
                    : <Badge variant="outline" className="text-muted-foreground">Off</Badge>}
                </div>
                <p className="text-sm text-muted-foreground mt-3">${Number(emp.hourly_rate).toFixed(2)}/hr</p>
                <div className="flex gap-2 mt-4">
                  <Button size="sm" data-testid={`clock-btn-${emp.id}`} onClick={() => toggleClock(emp)}
                    className={isIn ? "flex-1 bg-foreground hover:bg-foreground/90 text-[#FDFBF7]" : "flex-1 bg-secondary hover:bg-[#4B5845] text-secondary-foreground"}>
                    {isIn ? <><LogOut className="h-4 w-4 mr-1" /> Clock out</> : <><LogIn className="h-4 w-4 mr-1" /> Clock in</>}
                  </Button>
                  <Button size="icon" variant="ghost" className="text-destructive" onClick={() => remove(emp.id)} data-testid={`delete-employee-${emp.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Add staff member</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-1.5"><Label>Name</Label>
              <Input data-testid="emp-form-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="emp-form-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>Hourly rate ($)</Label>
                <Input type="number" step="0.01" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="emp-form-submit" className="bg-primary hover:bg-[#B35A42] text-primary-foreground">Add staff</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
